// Client for the asynchronous /data-agg job flow: submit, poll the status, read the preview rows.
// All network calls go through http.ts (auth header, ApiError, no logging of tokens or bodies).
import { DATA_API_BASE_URL } from '../config/api';
import { ApiError, describeJobError, getJson, isAbortError, postJson } from './http';

export type JobStatusValue = 'queued' | 'processing' | 'running' | 'completed' | 'failed';
export interface JobSubmitResponse {
  job_id: string;
  status: string;
  cached?: boolean;
  http_path?: string | null;
}
export interface JobStatusResponse {
  job_id: string;
  status: string;
  progress?: number;
  error?: unknown;
  /** ISO instants of the BigQuery job; null until the job has started or finished. */
  created_at?: string | null;
  start_time?: string | null;
  end_time?: string | null;
}

/** What a finished job hands back: the preview rows. (Full exports are an API feature, not read here.) */
export type JobOutcome<Row> = { kind: 'rows'; jobId: string; rows: Row[] };

export type JobErrorKind = 'failed' | 'timeout' | 'bad-response';

/** A job that ended without rows: the backend reported a failure, it ran too long, or it sent nonsense. */
export class JobError extends Error {
  readonly kind: JobErrorKind;
  readonly jobId: string | null;

  constructor(kind: JobErrorKind, message: string, jobId: string | null = null) {
    super(message);
    this.name = 'JobError';
    this.kind = kind;
    this.jobId = jobId;
    Object.setPrototypeOf(this, JobError.prototype);
  }
}

export interface JobTiming {
  pollIntervalMs: number;
  timeoutMs: number;
  maxPollErrors: number;
}
export const DEFAULT_JOB_TIMING: JobTiming = { pollIntervalMs: 1500, timeoutMs: 90_000, maxPollErrors: 3 };
/** Activities and Origin-Destination jobs scan more data than a Flow lookup and can run for minutes. */
export const LONG_JOB_TIMING: Partial<JobTiming> = { timeoutMs: 300_000 };

let timingOverride: Partial<JobTiming> | null = null;

/** Tests only: shrink the timing for every later run (smoke tests cannot pass options through a page). */
export function setJobTimingForTests(t: Partial<JobTiming> | null): void {
  timingOverride = t;
}

export interface RunJobOptions {
  signal?: AbortSignal;
  onStatus?: (s: JobStatusValue) => void;
  /** Called right after the job was accepted, before the first status poll. */
  onSubmitted?: (s: { jobId: string; cached: boolean }) => void;
  /** Called with every status the API sent (once with a bare "completed" when the submit answer already was). */
  onStatusDetail?: (s: JobStatusResponse) => void;
  timing?: Partial<JobTiming>;
}

const abortError = () => Object.assign(new Error('Cancelled'), { name: 'AbortError' });

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw abortError();
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/** POST /data-agg. */
export function submitJob(body: unknown, o?: { signal?: AbortSignal }): Promise<JobSubmitResponse> {
  return postJson<JobSubmitResponse>(DATA_API_BASE_URL, '/data-agg', body, { signal: o?.signal });
}

/** GET /data-agg/status/{id}. */
export function getJobStatus(jobId: string, o?: { signal?: AbortSignal }): Promise<JobStatusResponse> {
  return getJson<JobStatusResponse>(DATA_API_BASE_URL, `/data-agg/status/${encodeURIComponent(jobId)}`, {
    signal: o?.signal,
  });
}

const unexpectedShape = (jobId: string) =>
  new JobError('bad-response', 'The API sent results in an unexpected shape.', jobId);

/** GET /data-agg/results/{id}: the preview rows; anything else is a bad response. */
export async function getJobResult<Row>(jobId: string, o?: { signal?: AbortSignal }): Promise<JobOutcome<Row>> {
  const res = await getJson<{ preview?: unknown } | null>(
    DATA_API_BASE_URL,
    `/data-agg/results/${encodeURIComponent(jobId)}`,
    { signal: o?.signal },
  );
  const preview = res?.preview;
  if (Array.isArray(preview)) return { kind: 'rows', jobId, rows: preview as Row[] };
  throw unexpectedShape(jobId);
}

const isTransient = (e: unknown): boolean => e instanceof ApiError && (e.status === 0 || e.status >= 500);

/**
 * Submit, poll until the job finishes, then read the result. Rejects with an AbortError
 * (isAbortError is true), a JobError or an ApiError.
 *
 * Cancelling is client-side only: the backend has no cancel endpoint, so the BigQuery job keeps
 * running to completion. Aborting just stops this client from waiting for it.
 */
export async function runDataJob<Row>(body: unknown, o: RunJobOptions = {}): Promise<JobOutcome<Row>> {
  const timing: JobTiming = { ...DEFAULT_JOB_TIMING, ...timingOverride, ...o.timing };
  const controller = new AbortController();
  const { signal } = controller;
  let timedOut = false;
  let jobId: string | null = null;

  const onCallerAbort = () => controller.abort();
  if (o.signal) {
    if (o.signal.aborted) controller.abort();
    else o.signal.addEventListener('abort', onCallerAbort, { once: true });
  }
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timing.timeoutMs);

  try {
    throwIfAborted(signal);
    const submitted = await submitJob(body, { signal });
    throwIfAborted(signal);
    jobId = submitted.job_id;
    const id = submitted.job_id;
    o.onSubmitted?.({ jobId: id, cached: submitted.cached === true });

    if (submitted.status === 'completed') {
      o.onStatusDetail?.({ job_id: id, status: 'completed' });
    } else {
      let errorsInARow = 0;
      for (;;) {
        await sleep(timing.pollIntervalMs, signal);
        let status: JobStatusResponse;
        try {
          status = await getJobStatus(id, { signal });
        } catch (e) {
          throwIfAborted(signal);
          if (isAbortError(e)) throw e;
          if (isTransient(e) && ++errorsInARow <= timing.maxPollErrors - 1) continue;
          throw e;
        }
        throwIfAborted(signal);
        errorsInARow = 0;
        o.onStatus?.(status.status as JobStatusValue);
        o.onStatusDetail?.(status);
        if (status.status === 'completed') break;
        if (status.status === 'failed') throw new JobError('failed', describeJobError(status.error), id);
      }
    }

    const outcome = await getJobResult<Row>(id, { signal });
    throwIfAborted(signal);
    return outcome;
  } catch (e) {
    if (timedOut && !o.signal?.aborted) {
      const wait =
        timing.timeoutMs >= 120_000
          ? `${Math.round(timing.timeoutMs / 60_000)} minutes`
          : `${Math.round(timing.timeoutMs / 1000)} seconds`;
      throw new JobError('timeout', `The job took longer than ${wait}, so this page stopped waiting for it.`, jobId);
    }
    throw e;
  } finally {
    clearTimeout(timer);
    o.signal?.removeEventListener('abort', onCallerAbort);
  }
}

/** runDataJob for lookups that only need the job id and its rows (Flow). */
export async function runJob<Row>(body: unknown, o: RunJobOptions = {}): Promise<{ jobId: string; rows: Row[] }> {
  const outcome = await runDataJob<Row>(body, o);
  return { jobId: outcome.jobId, rows: outcome.rows };
}

/** Maps the rows of an outcome, dropping any the mapper refuses with null. */
export function mapOutcomeRows<A, B>(outcome: JobOutcome<A>, f: (row: A) => B | null): JobOutcome<B> {
  const rows: B[] = [];
  for (const row of outcome.rows) {
    const mapped = f(row);
    if (mapped !== null) rows.push(mapped);
  }
  return { kind: 'rows', jobId: outcome.jobId, rows };
}
